import { addDoc, collection } from 'firebase/firestore'
import { FieldError, Merge, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { db } from '../firebase'
import { Dropzone } from './dropzone'
import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from './layout'
import { Section } from './section'
import floatCool from '../assets/images/float-cool.png'
import milkAndMore from '../assets/images/milk-and-more.png'
import prize1 from '../assets/images/prize-1.png'
import prize2 from '../assets/images/prize-2.png'
import prize3 from '../assets/images/prize-3.png'
import prize4 from '../assets/images/prize-4.png'
import prize5 from '../assets/images/prize-5.png'
import prize6 from '../assets/images/prize-6.png'
import styles from '../assets/scss/components/form.module.scss'
import globalStyles from '../assets/scss/global.module.scss'
import classNames from 'classnames'

const ages = z.enum(['5-10', '11-17', '18+'])

const EntrySchema = z
  .object({
    age: ages,
    email: z.string().email(),
    file: z.string().url().optional(),
    floatName: z.string().array(),
    guardianName: z.string().optional(),
    name: z.string().min(1),
    postCode: z.string().min(1),
    reason: z.string().optional(),
    tel: z.string().min(1),
    terms: z.literal(true),
  })
  .refine((data) => data.floatName.filter((name) => !!name.trim()).length, {
    message: 'Please enter at least 1 name',
    path: ['floatName'],
  })
  .refine((data) => !data.floatName.filter((name) => name.length > 30).length, {
    message: 'Each name can be no longer than 30 characters',
    path: ['floatName'],
  })
  .refine((data) => data.age === '18+' || !!data.guardianName?.trim(), {
    message: 'Please enter your guardian’s full name',
    path: ['guardianName'],
  })

z.setErrorMap((issue, ctx) => {
  if (issue.code === z.ZodIssueCode.invalid_literal) {
    return { message: 'You must agree to the terms and conditions' }
  }

  if (issue.code === z.ZodIssueCode.invalid_type) {
    return { message: `Please select your ${issue.path}` }
  }

  if (issue.code === z.ZodIssueCode.too_small) {
    return { message: 'This field is required' }
  }

  return { message: ctx.defaultError }
})

export type Entry = z.infer<typeof EntrySchema>

export const Form: React.FC = () => {
  const navigate = useNavigate()
  const [isDisabled, setIsDisabled] = useState(false)
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<Entry>({
    resolver: zodResolver(EntrySchema),
  })
  const age = useWatch({ control, name: 'age' })
  const onSubmit: SubmitHandler<Entry> = async (entry) => {
    setIsDisabled(true)
    await addDoc(collection(db, 'entries'), { entry })
    navigate('/thanks')
  }

  return (
    <Layout>
      <Section>
        <div className={styles.details}>
          <h1 className={styles.h1}>Name our floats for the chance to win prizes</h1>
          <p className={styles.p}>
            Our milk floats drive through the night, to deliver milk and much more to our customers’ doorsteps. In fact,
            Milk &amp; More has one of the largest fleets of electric vehicles in the country, making half a million
            deliveries a week.
            <br />
            <strong>We need your help to give them a name. Do you have a fabulous pun or nickname in mind?</strong>
          </p>
          <img alt="" className={styles.floatCool} src={floatCool} />
          <img alt="" className={styles.milkAndMore} src={milkAndMore} />
          <p className={styles.p}>
            Join in the fun and fill in the entry form. There are prizes for the best names sent in. There’s even a Milk
            &amp; More offer for everyone who enters.
          </p>
          <p className={styles.p}>Competition closes 25.02.2023.</p>
          <div className={styles.prizes}>
            <h3 className={styles.h3}>Prizes include</h3>
            <div className={styles.prizeGrid}>
              <img alt="" src={prize1} />
              <img alt="" src={prize2} />
              <img alt="" className={styles.bigPrize} src={prize5} />
              <img alt="" className={styles.bigPrize} src={prize6} />
              <img alt="" src={prize3} />
              <img alt="" src={prize4} />
            </div>
            <p className={styles.p}>
              <strong>
                See{' '}
                <a href="https://www.milkandmore.co.uk/name-our-milk-float-tac" rel="noreferrer" target="_blank">
                  Terms &amp; Conditions
                </a>{' '}
                for full prize details
              </strong>
            </p>
          </div>
          <p className={styles.p}>
            <small>
              Complete the entry form at www.milkandmore.co.uk/name-our-milk-float, with your chosen name between 00:01
              23.01.2023 and 23:59 on the 25.02.2023 to enter. 1st, 2nd and 3rd prizes available for each age category
              (5-10 years, 11-17 years and 18 years and over). A parent/guardian must complete the form on behalf of any
              entrant under 18 years old. One entry per person. Winners will be determined by a panel of judges based on
              the entry criteria and the judges and Promotors decision will be final. 1st and 2nd prize winners for the
              5-10 and 11-17 years category will be required to attend a photoshoot. Open to residents of England, Wales
              &amp; Scotland. No purchase necessary. Full{' '}
              <a href="https://www.milkandmore.co.uk/name-our-milk-float-tac" rel="noreferrer" target="_blank">
                Terms &amp; Conditions
              </a>{' '}
              apply.
            </small>
          </p>
        </div>
        <div className={styles.formContainer}>
          <h2 className={styles.h2}>
            <strong>
              Help us name our electric milk floats, for a chance to win. See full{' '}
              <a href="https://www.milkandmore.co.uk/name-our-milk-float-tac" rel="noreferrer" target="_blank">
                Terms &amp; Conditions
              </a>{' '}
              for prizes and details – Good Luck!
            </strong>
          </h2>
          <form className={styles.form} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Field className={styles.ageField} error={errors.age}>
              Age *
              <div className={styles.radioButtons}>
                {ages.options.map((age, index) => (
                  <label className={styles.radioButtonLabel} key={age}>
                    <input
                      className={styles.radioButton}
                      tabIndex={index + 1}
                      type="radio"
                      value={age}
                      {...register('age')}
                    />
                    {age}
                  </label>
                ))}
              </div>
            </Field>
            <Field error={errors.floatName}>
              <div>You can enter up to 3 names. Max. 30 characters – including spaces *</div>
              {[...Array(3)].map((_, index) => (
                <input
                  autoComplete="new-password"
                  className={classNames(styles.textInput, styles.floatName)}
                  key={index}
                  tabIndex={index + 4}
                  {...register(`floatName.${index}`)}
                />
              ))}
            </Field>
            <Field error={errors.reason}>
              <label>
                <div>Is there a reason why you have chosen this name? (optional)</div>
                <textarea className={styles.textInput} tabIndex={7} {...register('reason')} />
              </label>
            </Field>
            <Dropzone setValue={setValue} />
            <Field error={errors.name}>
              <label>
                <div>Full name *</div>
                <input autoComplete="name" className={styles.textInput} tabIndex={9} {...register('name')} />
              </label>
            </Field>
            {age && age !== '18+' && (
              <Field error={errors.guardianName}>
                <label>
                  <div>Guardian’s full name *</div>
                  <input
                    autoComplete="new-password"
                    className={styles.textInput}
                    tabIndex={10}
                    {...register('guardianName')}
                  />
                </label>
              </Field>
            )}
            <Field error={errors.tel}>
              <label>
                <div>Telephone number *</div>
                <input autoComplete="tel" type="tel" className={styles.textInput} tabIndex={11} {...register('tel')} />
              </label>
            </Field>
            <Field error={errors.email}>
              <label>
                <div>Email *</div>
                <input
                  autoComplete="email"
                  type="email"
                  className={styles.textInput}
                  tabIndex={12}
                  {...register('email')}
                />
              </label>
            </Field>
            <Field error={errors.postCode}>
              <label>
                <div>UK Postcode *</div>
                <input
                  autoComplete="postal-code"
                  className={styles.textInput}
                  tabIndex={13}
                  {...register('postCode')}
                />
              </label>
            </Field>
            <Field error={errors.terms}>
              <label className={styles.checkboxLabel}>
                <input className={styles.checkbox} tabIndex={14} type="checkbox" {...register('terms')} />
                <div>
                  Tick here to accept our competition &nbsp;
                  <a href="https://www.milkandmore.co.uk/name-our-milk-float-tac" rel="noreferrer" target="_blank">
                    Terms &amp; Conditions
                  </a>
                  &nbsp; *
                </div>
              </label>
            </Field>
            <div className={styles.buttonContainer}>
              <button className={globalStyles.button} disabled={isDisabled}>
                Submit
              </button>
            </div>
          </form>
        </div>
      </Section>
    </Layout>
  )
}

const Field: React.FC<{
  children: ReactNode
  className?: string
  error?: Merge<FieldError, (FieldError | undefined)[]> | FieldError
}> = ({ className, children, error }) => {
  return (
    <>
      <div className={classNames(styles.field, className)}>{children}</div>
      <div className={styles.error}>{error?.message}</div>
    </>
  )
}
